import { Box, Input, Option, Select, Switch, Typography } from "@mui/joy";
import { Rating } from "@mui/material";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import moment from "moment";
import { CustomField } from "../../entities/customFieldGroup";
import { FieldLabel, tileColors } from "../formKit/formKit";

/**
 * Per-kind presentation metadata. Kind strings are the ones the Studio admin
 * app stores on the field definitions (NoteType.kind).
 */
export const customFieldKinds: Record<string, { icon: string; color: string }> =
  {
    textfield: { icon: "fa-solid fa-i-cursor", color: tileColors.blue },
    phone: { icon: "fa-solid fa-phone", color: tileColors.green },
    date: { icon: "fa-solid fa-calendar-days", color: tileColors.red },
    time: { icon: "fa-solid fa-clock", color: tileColors.orange },
    dateRange: { icon: "fa-solid fa-calendar-range", color: tileColors.indigo },
    color: { icon: "fa-solid fa-palette", color: tileColors.purple },
    boolean: { icon: "fa-solid fa-square-check", color: tileColors.teal },
    rating: { icon: "fa-solid fa-star", color: tileColors.orange },
    select: { icon: "fa-solid fa-rectangle-history", color: tileColors.gray },
  };

const defaultKind = { icon: "fa-solid fa-pen-field", color: tileColors.gray };

/**
 * Human-readable version of a stored custom-field value.
 *
 * @param kind field kind
 * @param value stored value
 * @returns {string} display string
 */
export function formatCustomFieldValue(kind?: string, value?: string): string {
  if (!value) return "";
  switch (kind) {
    case "date":
      return moment(value).format("ll");
    case "time":
      return moment(value).format("LT");
    case "dateRange":
      try {
        const range = JSON.parse(value);
        if (Array.isArray(range) && (range[0] || range[1])) {
          return `${range[0] ? moment(range[0]).format("ll") : "…"} – ${
            range[1] ? moment(range[1]).format("ll") : "…"
          }`;
        }
        return "";
      } catch (e) {
        return value;
      }
    case "boolean":
      return value === "true" ? "Yes" : "No";
    case "rating":
      return `${value}/5`;
    default:
      return value;
  }
}

const rightAlignedInputSx = {
  flex: 1,
  background: "transparent",
  "--Input-focusedThickness": "0px",
} as const;

const rightAlignedInputSlotProps = {
  input: { sx: { textAlign: "right", fontWeight: 600 } },
} as const;

/**
 * One custom-field row, iOS-Settings style: colored kind icon + field name on
 * the left, the value (or its editor) right-aligned. Kind-specific editor:
 * text, phone, date, time, date range, color, checkbox, star rating, select.
 *
 * @param root0 props
 * @param root0.field the field definition + current stored value
 * @param root0.edit whether the row is editable
 * @param root0.value current (possibly unsaved) value
 * @param root0.onChange fired with the new stored-format value
 * @returns {ReactElement} CustomFieldRow
 */
export function CustomFieldRow({
  field,
  edit,
  value,
  onChange,
}: {
  field: CustomField;
  edit: boolean;
  value?: string;
  onChange: (v: string) => void;
}) {
  const kind = customFieldKinds[field.kind ?? ""] ?? defaultKind;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        minHeight: 48,
      }}
    >
      <FieldLabel icon={kind.icon} color={kind.color} label={field.name} />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        {edit ? (
          <CustomFieldInput field={field} value={value} onChange={onChange} />
        ) : (
          <CustomFieldValue field={field} value={value} />
        )}
      </Box>
    </Box>
  );
}

/**
 * Read-only rendering of a field value.
 *
 * @param root0 props
 * @param root0.field the field definition
 * @param root0.value stored value
 * @returns {ReactElement} CustomFieldValue
 */
function CustomFieldValue({
  field,
  value,
}: {
  field: CustomField;
  value?: string;
}) {
  if (value && field.kind === "color") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{ width: 14, height: 14, borderRadius: "50%" }}
          style={{ background: value }}
        />
        <Typography level="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
    );
  }

  if (value && field.kind === "rating") {
    return <Rating readOnly value={parseInt(value) || 0} size="small" />;
  }

  const displayed = formatCustomFieldValue(field.kind, value);

  return (
    <Typography
      level="body2"
      noWrap
      sx={{
        textAlign: "right",
        fontWeight: 600,
        color: displayed
          ? "var(--mui-palette-text-primary)"
          : "var(--mui-palette-text-tertiary)",
      }}
    >
      {displayed || "Not set"}
    </Typography>
  );
}

/**
 * Kind-specific editor emitting values in the same stored format the Studio
 * admin app writes (ISO UTC datetimes, JSON [start, end] ranges, "true"/
 * "false" booleans, "1"–"5" ratings, hex colors).
 *
 * @param root0 props
 * @param root0.field the field definition
 * @param root0.value current value
 * @param root0.onChange fired with the new stored-format value
 * @returns {ReactElement} CustomFieldInput
 */
function CustomFieldInput({
  field,
  value,
  onChange,
}: {
  field: CustomField;
  value?: string;
  onChange: (v: string) => void;
}) {
  switch (field.kind) {
    case "phone":
      return (
        <Box sx={{ width: 200, height: 40 }} className="musician">
          <NerdPhoneField
            value={value ?? ""}
            onAccept={(e: string) => onChange(e)}
            textFieldProps={{
              fullWidth: true,
              variant: "standard",
              inputProps: {
                border: "none",
                disableUnderline: true,
                style: { textAlign: "right" },
              },
              size: "small",
              sx: { background: "transparent" },
            }}
          />
        </Box>
      );
    case "date":
      return (
        <Input
          type="date"
          variant="plain"
          value={value ? moment(value).format("YYYY-MM-DD") : ""}
          onChange={(e) =>
            onChange(
              e.target.value
                ? moment(e.target.value, "YYYY-MM-DD").utc().format()
                : ""
            )
          }
          sx={rightAlignedInputSx}
          slotProps={rightAlignedInputSlotProps}
        />
      );
    case "time":
      return (
        <Input
          type="time"
          variant="plain"
          value={value ? moment(value).format("HH:mm") : ""}
          onChange={(e) =>
            onChange(
              e.target.value
                ? moment(e.target.value, "HH:mm").utc().format()
                : ""
            )
          }
          sx={rightAlignedInputSx}
          slotProps={rightAlignedInputSlotProps}
        />
      );
    case "dateRange": {
      let range: (string | null)[] = [null, null];
      try {
        const parsed = JSON.parse(value || "[null, null]");
        if (Array.isArray(parsed)) range = parsed;
      } catch (e) {
        // keep the empty range
      }
      const setPart = (index: number, part: string) => {
        const next = [...range];
        next[index] = part ? moment(part, "YYYY-MM-DD").utc().format() : null;
        onChange(JSON.stringify(next));
      };
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Input
            type="date"
            variant="plain"
            value={range[0] ? moment(range[0]).format("YYYY-MM-DD") : ""}
            onChange={(e) => setPart(0, e.target.value)}
            sx={{ background: "transparent" }}
            size="sm"
          />
          <Typography level="body2">–</Typography>
          <Input
            type="date"
            variant="plain"
            value={range[1] ? moment(range[1]).format("YYYY-MM-DD") : ""}
            onChange={(e) => setPart(1, e.target.value)}
            sx={{ background: "transparent" }}
            size="sm"
          />
        </Box>
      );
    }
    case "color":
      return (
        <Input
          type="color"
          variant="plain"
          value={value && value.startsWith("#") ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          sx={{ width: 64, background: "transparent" }}
        />
      );
    case "boolean":
      return (
        <Switch
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
        />
      );
    case "rating":
      return (
        <Rating
          value={value ? parseInt(value) || 0 : 0}
          onChange={(e, v) => onChange(v ? `${v}` : "")}
        />
      );
    case "select":
      return (
        <Select
          variant="plain"
          size="sm"
          value={value ?? ""}
          onChange={(e, v) => {
            if (v) onChange(v);
          }}
          placeholder="Select an option"
          sx={{ background: "transparent" }}
        >
          {(field.data ?? "")
            .split(",")
            .filter((option) => option.length)
            .map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
        </Select>
      );
    default:
      return (
        <Input
          variant="plain"
          value={value ?? ""}
          placeholder="Not set"
          onChange={(e) => onChange(e.target.value)}
          sx={rightAlignedInputSx}
          slotProps={rightAlignedInputSlotProps}
        />
      );
  }
}
